import { toast } from '@popsure/dirty-swan';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useSafeTranslation } from 'shared/i18n';

import {
  updateEmail,
  verifyMagicLink,
  VerifyMagicLinkResponse,
} from '../../api';
import { getErrorMessage } from '../../utils/getApiErrors';
import onError from '../../utils/onError';
import { ConfirmView } from './Confirm.view';
import { ConfirmLinkExpired } from './ConfirmLinkExpired';
import { ConfirmLinkNotFound } from './ConfirmLinkNotFound';

export const ConfirmContainer = () => {
  const { magicLinkToken } = useParams<{ magicLinkToken: string }>();
  const history = useHistory();
  const { t } = useSafeTranslation();

  const showSuccessToast = () => {
    toast(
      t('updateEmail.updateConfirmation.success', 'Email address was updated'),
      {
        type: 'success',
      }
    );
  };

  const {
    mutate: verifyMagicLinkMutate,
    error: magicLinkError,
    data: magicLinkData,
  } = useMutation<VerifyMagicLinkResponse>({
    mutationFn: () => verifyMagicLink(magicLinkToken),
    onError: (error) =>
      onError({
        error,
        message: 'Error occurred while verifying magic link',
      }),
  });

  useEffect(() => {
    verifyMagicLinkMutate();
  }, [verifyMagicLinkMutate]);

  const {
    mutate: updateEmailMutate,
    isLoading: isUpdatingEmail,
    error: updateEmailError,
  } = useMutation({
    mutationFn: (requestId: string) => updateEmail(requestId),
    onSuccess: () => {
      showSuccessToast();
      history.push('/me/account-settings');
    },
    onError: (error) =>
      onError({
        error,
        requestId: magicLinkData?.requestId,
        message: 'Error occurred while updating email',
      }),
  });

  if (
    magicLinkError &&
    getErrorMessage(magicLinkError) === 'REQUEST_LINK_EXPIRED'
  ) {
    return <ConfirmLinkExpired />;
  }

  if (
    magicLinkError &&
    getErrorMessage(magicLinkError) === 'REQUEST_NOT_FOUND'
  ) {
    return <ConfirmLinkNotFound />;
  }

  return (
    <ConfirmView
      email={magicLinkData?.newEmail}
      onUpdateEmail={() => updateEmailMutate(magicLinkData?.requestId ?? '')}
      isLoading={isUpdatingEmail}
      error={updateEmailError}
    />
  );
};
